/**
 * The kit's working copy (localStorage `stonefall.kit`): the cave on the bench, kept between
 * visits whether or not it was saved to the library.
 */

import type { CaveDef } from '../../caves/types';
import type { KeyValueStore } from '../../platform/storage';

export const WORKING_COPY_KEY = 'stonefall.kit';

export interface WorkingCopy {
  readonly def: CaveDef;
  /** The library entry it was loaded from or saved to. */
  readonly key?: string;
  /** Changed since it was last saved or loaded. */
  readonly dirty: boolean;
}

function isDef(v: unknown): v is CaveDef {
  if (typeof v !== 'object' || v === null) return false;
  const d = v as Record<string, unknown>;
  return (
    typeof d['name'] === 'string' &&
    Array.isArray(d['map']) &&
    (d['map'] as unknown[]).every((r) => typeof r === 'string') &&
    Array.isArray(d['levels']) &&
    Array.isArray(d['colors'])
  );
}

export function loadWorkingCopy(store: KeyValueStore): WorkingCopy | null {
  try {
    const raw = JSON.parse(store.load(WORKING_COPY_KEY) ?? 'null') as unknown;
    if (typeof raw !== 'object' || raw === null) return null;
    const r = raw as Record<string, unknown>;
    if (!isDef(r['def'])) return null;
    const key = typeof r['key'] === 'string' ? r['key'] : undefined;
    return { def: r['def'], dirty: r['dirty'] !== false, ...(key === undefined ? {} : { key }) };
  } catch {
    return null;
  }
}

export function saveWorkingCopy(store: KeyValueStore, copy: WorkingCopy): void {
  store.save(WORKING_COPY_KEY, JSON.stringify(copy));
}
